<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h2>Reserves</h2>
          <button class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadDashboard(true)">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="dashboard && dashboard.activeAlerts && dashboard.activeAlerts.length" class="col-xl-12">
      <div v-for="alert in dashboard.activeAlerts" :key="alert.id"
           class="alert d-flex justify-content-between align-items-center flex-wrap gap-2"
           :class="alert.severity === 'CRITICAL' ? 'alert-danger' : 'alert-warning'">
        <div>
          <strong>{{ alertKindLabel(alert.alertKind) }} &mdash; {{ alert.severity }}</strong>
          <div class="small">{{ alert.detail }}</div>
        </div>
        <button class="btn btn-sm btn-light"
                :disabled="acknowledging === alert.id || alert.status === 'ACKNOWLEDGED'"
                @click="acknowledgeAlert(alert)">
          <span v-if="acknowledging === alert.id" class="spinner-border spinner-border-sm me-2"></span>
          {{ alert.status === 'ACKNOWLEDGED' ? 'Acknowledged' : (acknowledging === alert.id ? 'Acknowledging...' : 'Acknowledge') }}
        </button>
      </div>
    </div>

    <div v-if="!dashboard" class="col-xl-12 text-center py-4">
      <span class="spinner-border spinner-border-sm me-2"></span> Loading reserves...
    </div>

    <template v-else>
      <div class="col-xl-12">
        <div class="card mb-3">
          <div class="card-body">
            <h6 class="text-muted mb-1">Net position genuinely ours</h6>
            <h2 class="mb-1" :class="netPositionClass">{{ netPositionDisplay }}</h2>
            <p class="text-muted small mb-0">
              Operating balance ({{ operatingTotalDisplay }}) minus outstanding obligations
              ({{ currency(dashboard.totalOutstandingObligations) }})
              <span v-if="!dashboard.totalOutstandingObligationsComplete">
                &mdash; excludes {{ (dashboard.excludedPools || []).join(', ') }} (unavailable)
              </span>
            </p>
            <p class="text-muted small mb-0" v-if="dashboard.operatingBalance && dashboard.operatingBalance.available">
              Stripe platform balance (available + pending), as of {{ dateTime(dashboard.operatingBalance.asOf) }}.
              Excludes cash already paid out to Asher's bank.
            </p>
            <p class="text-danger small mb-0" v-else>
              Operating balance unavailable{{ dashboard.operatingBalance && dashboard.operatingBalance.unavailableReason ? ': ' + dashboard.operatingBalance.unavailableReason : '' }}
              &mdash; alerts are skipped while this is the case.
            </p>
            <p class="text-muted small mb-0" v-if="dashboard.coverageRatio !== null && dashboard.coverageRatio !== undefined">
              Coverage ratio: {{ Number(dashboard.coverageRatio).toFixed(2) }}x
            </p>
          </div>
        </div>
      </div>

      <div class="col-xl-4 col-md-6">
        <ReservePoolCard title="Insurance reserve" :available="dashboard.insurance.available"
                          :unavailable-reason="dashboard.insurance.unavailableReason">
          <table class="table table-sm mb-2">
            <tbody>
              <tr><th>Accrued</th><td>{{ currency(dashboard.insurance.accrued) }}</td></tr>
              <tr><th>Swept</th><td>{{ currency(dashboard.insurance.swept) }}</td></tr>
              <tr><th>Paid</th><td>{{ currency(dashboard.insurance.paid) }}</td></tr>
              <tr><th>At risk (unswept)</th><td>{{ currency(dashboard.insurance.atRisk) }}</td></tr>
            </tbody>
          </table>
          <div v-if="dashboard.insurance.projectionAvailable" class="border-top pt-2">
            <h6 class="text-muted mb-1">
              Projected true-up <span class="badge bg-light text-dark">projection, not a bill</span>
            </h6>
            <p class="mb-1"><strong>{{ currency(dashboard.insurance.projectedTrueUp) }}</strong></p>
            <p class="text-muted small mb-0">
              Run rate {{ currency(dashboard.insurance.dailyRunRate) }}/day over the trailing
              {{ dashboard.insurance.trailingWindowDays }} days,
              {{ dashboard.insurance.daysRemainingInPolicyPeriod }} day(s) remaining in the policy period
              (ends {{ dashboard.insurance.policyPeriodEndDate }}).
            </p>
          </div>
          <div v-else class="border-top pt-2 text-muted small">
            Projected true-up not available{{ dashboard.insurance.projectionUnavailableReason ? ': ' + dashboard.insurance.projectionUnavailableReason : '' }}.
          </div>
        </ReservePoolCard>
      </div>

      <div class="col-xl-4 col-md-6">
        <ReservePoolCard title="HST reserve" :available="dashboard.tax.available"
                          :unavailable-reason="dashboard.tax.unavailableReason">
          <table class="table table-sm mb-0">
            <tbody>
              <tr><th>Accrued</th><td>{{ currency(dashboard.tax.accrued) }}</td></tr>
              <tr><th>Swept</th><td>{{ currency(dashboard.tax.swept) }}</td></tr>
              <tr><th>Remitted</th><td>{{ currency(dashboard.tax.remitted) }}</td></tr>
              <tr><th>Net payable</th><td>{{ currency(dashboard.tax.netPayable) }}</td></tr>
              <tr><th>Not yet swept</th><td>{{ currency(dashboard.tax.unswept) }}</td></tr>
              <tr><th>Next filing date</th><td>{{ dashboard.tax.nextFilingDate || 'N/A' }} ({{ dashboard.tax.filingFrequency }})</td></tr>
            </tbody>
          </table>
          <div v-if="dashboard.tax.settledRidesMissingAccrualCount > 0" class="alert alert-warning mt-2 mb-0 py-1 px-2 small">
            {{ dashboard.tax.settledRidesMissingAccrualCount }} settled ride(s) missing an HST accrual.
          </div>
        </ReservePoolCard>
      </div>

      <div class="col-xl-4 col-md-6">
        <ReservePoolCard title="Wallet liability" :available="true">
          <table class="table table-sm mb-2">
            <tbody>
              <tr><th>Rider funds (refundable)</th><td>{{ currency(dashboard.wallet.riderFunds) }}</td></tr>
              <tr><th>Promotional credit (not refundable)</th><td>{{ currency(dashboard.wallet.promotionalCredit) }}</td></tr>
              <tr><th>Total wallet liability</th><td>{{ currency(dashboard.wallet.totalLiability) }}</td></tr>
            </tbody>
          </table>
          <p class="text-muted small">
            Only rider funds count against the operating balance above &mdash; promotional credit is
            Asher's own promised discount, not cash owed back.
          </p>
          <table class="table table-sm mb-0">
            <tbody>
              <tr><th>Purchased funds</th><td>{{ currency(dashboard.wallet.purchasedFunds) }}</td></tr>
              <tr><th>Refund credit</th><td>{{ currency(dashboard.wallet.refundCredit) }}</td></tr>
              <tr><th>Promotional credit</th><td>{{ currency(dashboard.wallet.promotionalCreditOnly) }}</td></tr>
              <tr><th>Support credit</th><td>{{ currency(dashboard.wallet.supportCredit) }}</td></tr>
              <tr><th>Referral credit</th><td>{{ currency(dashboard.wallet.referralCredit) }}</td></tr>
            </tbody>
          </table>
          <div v-if="dashboard.wallet.hasDrift" class="alert alert-warning mt-2 mb-0 py-1 px-2 small">
            Category totals disagree with the wallet ledger cross-check by {{ currency(dashboard.wallet.driftAmount) }}.
          </div>
        </ReservePoolCard>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import ReservePoolCard from './ReservePoolCard.vue';
import { currency as currencyRenderer } from '../utility/DatatableRenderer';

const dashboard = ref(null);
const isLoading = ref(false);
const acknowledging = ref(null);

function currency(value) {
  return currencyRenderer(value);
}

function dateTime(value) {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString();
}

function alertKindLabel(kind) {
  return { RESERVE_COVERAGE: 'Reserve coverage', SWEEP_LAG: 'Sweep lag' }[kind] ?? kind;
}

const netPositionDisplay = computed(() => {
  if (!dashboard.value || !dashboard.value.operatingBalance || !dashboard.value.operatingBalance.available
      || dashboard.value.netPositionGenuinelyOurs === null || dashboard.value.netPositionGenuinelyOurs === undefined) {
    return '—';
  }
  return currency(dashboard.value.netPositionGenuinelyOurs);
});

const operatingTotalDisplay = computed(() => {
  if (!dashboard.value || !dashboard.value.operatingBalance || !dashboard.value.operatingBalance.available) {
    return '—';
  }
  return currency(dashboard.value.operatingBalance.total);
});

const netPositionClass = computed(() => {
  if (!dashboard.value || dashboard.value.netPositionGenuinelyOurs === null
      || dashboard.value.netPositionGenuinelyOurs === undefined) {
    return '';
  }
  return Number(dashboard.value.netPositionGenuinelyOurs) < 0 ? 'text-danger' : 'text-success';
});

async function loadDashboard(refresh) {
  isLoading.value = true;
  try {
    const response = await fetch(`/api/admin/reserves/summary${refresh ? '?refresh=true' : ''}`);
    if (response.ok) {
      dashboard.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading the reserve dashboard:', error);
  } finally {
    isLoading.value = false;
  }
}

async function acknowledgeAlert(alert) {
  acknowledging.value = alert.id;
  try {
    const response = await fetch(`/api/admin/reserves/alerts/${alert.id}/acknowledge`, { method: 'POST' });
    if (response.ok) {
      await loadDashboard(true);
    }
  } catch (error) {
    console.error('Error acknowledging reserve alert:', error);
  } finally {
    acknowledging.value = null;
  }
}

onMounted(() => {
  loadDashboard(false);
});
</script>
